import { ReactElement, ReactNode, CSSProperties, useCallback, useMemo, useEffect, useRef, memo, FC, useState, DependencyList, RefObject } from 'react'
import { Trans } from 'react-i18next'

import { closestCenter, DndContext } from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, rectSortingStrategy } from '@dnd-kit/sortable'
import { CircularProgress, Table, TableBody, TableCellProps, TableHead, TableProps, debounce } from '@mui/material'
import { useVirtualizer, useWindowVirtualizer } from '@tanstack/react-virtual'

import { stableSort } from '../../helpers/arrays'
import analyticsService from '../../services/AnalyticsService'
import { AnalyticsProviders } from '../../types/IAnalyticsProvider'
import './GRTable.scss'
import { HeaderRow } from './HeaderRow/HeaderRow'
import { SectionColumnHeaderRow } from './SectionColumnHeaderRow/SectionColumnHeaderRow'
import { SectionHeaderRow } from './SectionHeaderRow/SectionHeaderRow'
import { StatusRow } from './StatusRow/StatusRow'
import { VirtualRow } from './VirtualRow/VirtualRow'
import { getChildColumns, findParentColumn } from './helpers/tableHelpers'
import { SortOrder } from './types/SortOrder'
import { TableSortValues } from './types/TableSortValues'

export { SortOrder, TableSortValues }

/**
 * Component for representing tabular data. Supports displaying data as more complex components and sorting by columns
 */

type SortAccessor<RowType, CustomPropsType, ColumnIdType> =
  | string
  | (({
      row,
      customTableProps,
      col,
    }: {
      row: RowType
      customTableProps?: CustomPropsType
      col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>
    }) => string | number | null)

export type GRTableColumn<RowType, CustomPropsType, ColumnIdType = void> = {
  labelAccessor?:
    | string
    | ReactElement
    | (({
        col,
        handleSort,
        customTableProps,
        columns,
        sectionId,
      }: {
        col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>
        handleSort?: (column: GRTableColumn<RowType, CustomPropsType, ColumnIdType>) => void
        customTableProps?: CustomPropsType
        columns?: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
        // the section id of the row group this header belongs to, set only when the label is
        // rendered by a repeated per-section column header (see repeatColumnHeadersPerSection)
        sectionId?: string
      }) => ReactElement | string)
  accessor?: string | (({ row, rowIndex, customTableProps }: { row: RowType; rowIndex: number; customTableProps?: CustomPropsType }) => ReactNode)
  sortable?: boolean | 'manual'
  sortOrder?: SortOrder
  sortAccessor?: SortAccessor<RowType, CustomPropsType, ColumnIdType> | SortAccessor<RowType, CustomPropsType, ColumnIdType>[]
  sortAnalyticsEvent?: {
    eventName: string // name of the event to be sent to analytics (mandatory)
    eventData?: any // extra data to be sent to analytics (optional)
  }
  defaultSortOrder?: SortOrder
  sortFn?: (a: unknown, b: unknown) => number
  width?: number
  cellProps?: TableCellProps
  headerCellProps?: TableCellProps
  hidden?:
    | boolean
    | (({ col, customTableProps }: { col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>; customTableProps?: CustomPropsType }) => boolean)
  columns?: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
  // column id is required only if you need dynamic columns for your table
  id?: ColumnIdType
  // use this prop only if you need to override the column selectors column label retrieved by labelAccessor
  columnSelectorLabelAccessor?:
    | string
    | (({ col, customTableProps }: { col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>; customTableProps?: CustomPropsType }) => string)
  // this prop can be used to set the first column of the table sticky
  sticky?: boolean
}

export type GRTableProps<RowType, CustomPropsType, ColumnIdType = void> = {
  /**
   * Array of data rows to display in the table.
   * Note: This won't work with useFieldArray from React Hook Form due to virtualization
   * and the way the component handles data updates.
   */
  rows?: RowType[]
  columns: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
  onColumnsUpdated: (cols: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]) => void
  getRowClass?: (row: RowType, index: number) => string
  getRowStyle?: (row: RowType, index: number) => CSSProperties
  scroller?: RefObject<HTMLElement>
  hoverable?: boolean
  striped?: boolean
  tableProps?: TableProps
  noRowsLabel?: ReactNode
  isLoading?: boolean
  defaultSortOrder?: SortOrder
  virtualOverscan?: number
  customProps?: CustomPropsType
  selectedColumns?: ColumnIdType[]
  maxRows?: number
  gridlines?: boolean
  onSortRows?: (rows: RowType[]) => void
  sortable?: boolean
  disableSortTooltip?: string
  rowIdKey?: string | ((row: RowType, index?: number) => string)
  // groups rows into sections by the returned id. A full width section header row is rendered
  // whenever the section changes between consecutive (sorted) rows, but only when the rows
  // contain more than one distinct section. Requires renderSectionHeader to be set.
  getRowSectionId?: (row: RowType) => string
  // renders the content of a section header row for the given section id
  renderSectionHeader?: ({ sectionId, count }: { sectionId: string; count: number }) => ReactNode
  // when true, section header rows become clickable and toggle the visibility of their rows (accordion behavior)
  collapsibleSections?: boolean
  // section ids that should start out collapsed when collapsibleSections is enabled
  defaultCollapsedSectionIds?: string[]
  // when true, a non-interactive column header row is repeated at the start of each expanded section
  repeatColumnHeadersPerSection?: boolean
  // hides the top column header row. Column widths are normally measured from the top header,
  // so this should only be used together with repeatColumnHeadersPerSection, which takes over
  // the width measurement to keep the virtualized rows aligned.
  hideHeader?: boolean
}

type GRTableDisplayItem<RowType> =
  | { type: 'row'; row: RowType; rowIndex: number }
  | { type: 'section'; sectionId: string; count: number }
  | { type: 'sectionColumnHeader'; sectionId: string }

export const Component = <RowType extends object, CustomPropsType extends object | undefined, ColumnIdType extends string | void>({
  rows,
  columns,
  hoverable,
  striped,
  tableProps,
  onColumnsUpdated,
  getRowClass,
  getRowStyle,
  scroller,
  noRowsLabel = <Trans i18nKey="common:no_rows" />,
  isLoading,
  defaultSortOrder = SortOrder.DESC,
  virtualOverscan = 50,
  customProps,
  selectedColumns,
  maxRows,
  gridlines = false,
  onSortRows,
  sortable,
  disableSortTooltip,
  rowIdKey,
  getRowSectionId,
  renderSectionHeader,
  collapsibleSections,
  defaultCollapsedSectionIds,
  repeatColumnHeadersPerSection,
  hideHeader,
}: GRTableProps<RowType, CustomPropsType, ColumnIdType>) => {
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<Set<string>>(() => new Set(defaultCollapsedSectionIds))

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSectionIds((current) => {
      const updated = new Set(current)
      if (updated.has(sectionId)) {
        updated.delete(sectionId)
      } else {
        updated.add(sectionId)
      }
      return updated
    })
  }, [])

  // function for accessing individual cell value
  const getCellValue = useCallback(
    ({ row, col, rowIndex }: { row: RowType; col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>; rowIndex: number }) => {
      if (col.accessor) {
        if (typeof col.accessor === 'function') {
          return col.accessor({ row, rowIndex, customTableProps: customProps })
        } else if (typeof col.accessor === 'string') {
          return row[col.accessor as keyof RowType] as ReactNode
        }
      }
    },
    [customProps]
  )

  // function for accessing sorting value for a specified column
  const getSortValue = useCallback(
    ({
      row,
      col,
      rowIndex,
      sortAccessor,
    }: {
      row: RowType
      col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>
      rowIndex: number
      sortAccessor?: SortAccessor<RowType, CustomPropsType, ColumnIdType>
    }) => {
      if (typeof sortAccessor === 'function') {
        return sortAccessor({ row, customTableProps: customProps, col })
      } else if (typeof sortAccessor === 'string') {
        return row[sortAccessor as keyof RowType]
      } else {
        return getCellValue({ row, col, rowIndex })
      }
    },
    [getCellValue, customProps]
  )

  const getColumnHiddenValue = useCallback(
    (col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>) => {
      // first check if the column is hiden according to column configuration...
      let isHidden = false
      if (typeof col.hidden === 'function') {
        isHidden = col.hidden({ col, customTableProps: customProps })
      } else {
        isHidden = !!col.hidden
      }

      // ...and if not check if it should be hidden according to selected columns
      if (!isHidden && selectedColumns && col.id) {
        return !selectedColumns.includes(col.id)
      } else {
        return isHidden
      }
    },
    [customProps, selectedColumns]
  )

  const { forcedCallback: estimateSize, forceUpdate } = useForcedCallback(() => 100, [])

  // find a given column from current columns
  const findColumn = useCallback(
    (
      columns: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[],
      column: GRTableColumn<RowType, CustomPropsType, ColumnIdType>
    ): GRTableColumn<RowType, CustomPropsType, ColumnIdType> | undefined => {
      const found = columns.find((col) => col === column)
      if (!found && columns.length > 0) {
        return findColumn(
          columns.flatMap((col) => col.columns).filter((childCol) => !!childCol) as GRTableColumn<RowType, CustomPropsType, ColumnIdType>[],
          column
        )
      } else {
        return found
      }
    },
    []
  )

  // handler for clicking headers (sorting)
  const handleHeaderClick = useCallback(
    (col: GRTableColumn<RowType, CustomPropsType, ColumnIdType>) => {
      if (col.sortAnalyticsEvent) {
        analyticsService.trackEvent(col.sortAnalyticsEvent.eventName, {
          data: {
            column: typeof col.labelAccessor === 'string' ? col.labelAccessor : '',
            sortOrder: col.sortOrder,
            gameName: col.sortAnalyticsEvent.eventData?.game.resolvedName,
          },
          serviceToExclude: [AnalyticsProviders.hubspot, AnalyticsProviders.intercom],
        })
      }

      // recursive function for resetting the sort order of all columns except given column
      const resetColumnSort = (
        columns: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[],
        column: GRTableColumn<RowType, CustomPropsType, ColumnIdType>
      ): GRTableColumn<RowType, CustomPropsType, ColumnIdType>[] => {
        if (columns.length > 0) {
          return columns.map((col) => (col !== column ? { ...col, sortOrder: undefined, columns: resetColumnSort(col.columns || [], column) } : column))
        } else {
          return columns
        }
      }

      // find the clicked column and change the sort order + reset sort order for other columns
      const found = findColumn(columns, col)
      if (found) {
        const newSortOrder =
          found.sortOrder === SortOrder.ASC ? SortOrder.DESC : found.sortOrder === SortOrder.DESC ? SortOrder.ASC : found.defaultSortOrder || defaultSortOrder
        found.sortOrder = newSortOrder
        const resetColumns = resetColumnSort(columns, found)

        // report the result to parent
        onColumnsUpdated(resetColumns)
      }
    },
    [columns, onColumnsUpdated, defaultSortOrder, findColumn]
  )

  // sort table rows according to sort order defined in columns
  const tableRows = useMemo(() => {
    let copyOfRows = rows ? [...rows] : []

    // recursively sort rows according to rules defined in columns
    const sort = (columns: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]) => {
      columns.forEach((col) => {
        if (col.sortable && col.sortOrder) {
          const orderCoefficient = col.sortOrder === SortOrder.ASC ? 1 : -1
          const sorters = Array.isArray(col.sortAccessor) ? col.sortAccessor : [col.sortAccessor]

          // sort the rows by each sorter function or field in column configuration
          sorters.forEach((sorter) => {
            copyOfRows = stableSort(copyOfRows, (a, b) => {
              const valueA = getSortValue({ row: a, col, rowIndex: 0, sortAccessor: sorter }) ?? 0
              const valueB = getSortValue({ row: b, col, rowIndex: 0, sortAccessor: sorter }) ?? 0

              // preserve stable order when both rows share the same pinned position
              if (
                (valueA === TableSortValues.AlwaysFirst && valueB === TableSortValues.AlwaysFirst) ||
                (valueA === TableSortValues.AlwaysLast && valueB === TableSortValues.AlwaysLast)
              ) {
                return 0
              }

              if (valueB === TableSortValues.AlwaysFirst || valueA === TableSortValues.AlwaysLast) {
                return 1
              }

              if (valueA === TableSortValues.AlwaysFirst || valueB === TableSortValues.AlwaysLast) {
                return -1
              }

              // use a custom sort function given for the column
              if (col.sortFn) {
                return col.sortFn(valueA, valueB) * orderCoefficient
              }

              if (typeof valueA === 'string' && typeof valueB === 'string') {
                return valueB.localeCompare(valueA) * orderCoefficient // NOTE: DO NOT TOUCH THIS! FIX SORTING ORDER OTHERWISE OR YOU BREAK A LOT OF THINGS!
              }

              if (valueA < valueB) {
                return -1 * orderCoefficient
              }

              if (valueA > valueB) {
                return 1 * orderCoefficient
              }

              return 0
            })
          })
        }

        if (col.columns?.length) {
          sort(col.columns || [])
        }
      })
    }

    sort(columns)

    return maxRows ? copyOfRows.slice(0, maxRows) : copyOfRows
  }, [rows, columns, maxRows, getSortValue])

  // list of items to render: plain rows, optionally interleaved with section header rows
  const displayItems = useMemo((): GRTableDisplayItem<RowType>[] => {
    const rowItems = tableRows.map((row, rowIndex): GRTableDisplayItem<RowType> => ({ type: 'row', row, rowIndex }))

    if (!getRowSectionId || !renderSectionHeader) return rowItems

    const sectionCounts = tableRows.reduce((acc, row) => {
      const sectionId = getRowSectionId(row)
      acc[sectionId] = (acc[sectionId] || 0) + 1
      return acc
    }, {} as { [sectionId: string]: number })

    // no headers needed when all rows belong to the same section
    if (Object.keys(sectionCounts).length < 2) return rowItems

    let previousSectionId: string | undefined

    return tableRows.reduce((acc, row, rowIndex) => {
      const sectionId = getRowSectionId(row)
      const isExpanded = !collapsibleSections || !collapsedSectionIds.has(sectionId)
      if (sectionId !== previousSectionId) {
        acc.push({ type: 'section', sectionId, count: sectionCounts[sectionId] })
        if (repeatColumnHeadersPerSection && isExpanded) {
          acc.push({ type: 'sectionColumnHeader', sectionId })
        }
        previousSectionId = sectionId
      }
      if (isExpanded) {
        acc.push({ type: 'row', row, rowIndex })
      }
      return acc
    }, [] as GRTableDisplayItem<RowType>[])
  }, [tableRows, getRowSectionId, renderSectionHeader, collapsibleSections, collapsedSectionIds, repeatColumnHeadersPerSection])

  const debouncedForcedUpdate = useMemo(() => {
    return debounce(() => {
      forceUpdate()
    }, 300)
  }, [forceUpdate])

  // we will forcefully update the table when rows or columns change
  useEffect(() => {
    debouncedForcedUpdate()
    return () => debouncedForcedUpdate.clear()
  }, [rows, columns, debouncedForcedUpdate])

  // mark width for column based on measuring the headers
  const handleColWidthMeasuring = useCallback(
    (column: GRTableColumn<RowType, CustomPropsType, ColumnIdType>, colWidth: number) => {
      const copyOfColumns = [...columns]
      const foundColumn = findColumn(copyOfColumns, column)
      if (foundColumn && colWidth !== foundColumn?.width) {
        foundColumn.width = colWidth
        onColumnsUpdated(copyOfColumns)
      }
    },
    [columns, onColumnsUpdated, findColumn]
  )

  const rowClass = useCallback((row: RowType, index: number): string => (getRowClass ? getRowClass(row, index) : ''), [getRowClass])
  const rowStyle = useCallback((row: RowType, index: number) => (getRowStyle ? getRowStyle(row, index) : {}), [getRowStyle])

  const getRowKey = useCallback(
    (index: number): string => {
      const item = displayItems[index]
      if (item?.type === 'section') {
        return `section-${item.sectionId}`
      }
      if (item?.type === 'sectionColumnHeader') {
        return `section-columns-${item.sectionId}`
      }
      if (!rowIdKey || !item) {
        return String(index)
      }
      if (typeof rowIdKey === 'string') {
        return String((item.row as Record<string, unknown>)[rowIdKey])
      }
      return rowIdKey(item.row, index)
    },
    [displayItems, rowIdKey]
  )

  const headRef = useRef<HTMLTableSectionElement>(null)

  // control when to show horizontal scrollbar and adjust container height accordingly
  const virtualContainerRef = useRef<HTMLDivElement>(null)
  const tableBodyRef = useRef<HTMLTableSectionElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)

  // auto-detect MUI DialogContent as scroll container when scroller prop is not provided
  const dialogContentScrollerRef = useRef<HTMLElement | null>(null)
  const showHorizontalScroll = virtualContainerRef.current?.offsetWidth !== tableRef.current?.offsetWidth
  // measure horizontal scrollbar height to compensate container height
  const scrollbarHeight = (virtualContainerRef.current?.offsetHeight || 0) - (virtualContainerRef.current?.clientHeight || 0)

  useEffect(() => {
    if (!scroller && virtualContainerRef.current) {
      dialogContentScrollerRef.current = virtualContainerRef.current.closest('.MuiDialogContent-root') as HTMLElement | null
    }
  }, [scroller])

  const windowVirtualizer = useWindowVirtualizer({
    count: isLoading ? 0 : displayItems.length,
    overscan: virtualOverscan,
    estimateSize: estimateSize,
    scrollMargin: (virtualContainerRef.current?.offsetTop ?? 0) + (tableBodyRef.current?.offsetTop ?? 0),
    ...(rowIdKey ? { getItemKey: (index: number) => getRowKey(index) } : {}),
  })

  const virtualizer = useVirtualizer({
    count: isLoading ? 0 : displayItems.length,
    getScrollElement: () => scroller?.current ?? dialogContentScrollerRef.current ?? null,
    overscan: virtualOverscan,
    estimateSize: estimateSize,
    scrollMargin: (virtualContainerRef.current?.offsetTop ?? 0) + (tableBodyRef.current?.offsetTop ?? 0),
    ...(rowIdKey ? { getItemKey: (index: number) => getRowKey(index) } : {}),
  })

  const hasCustomScrollParent = !!(scroller?.current || dialogContentScrollerRef.current)
  const rowVirtualizer = hasCustomScrollParent ? virtualizer : windowVirtualizer

  useEffect(() => {
    // use debounced function to avoid excessive calls on callback
    const onResize = debouncedForcedUpdate
    window.addEventListener('resize', onResize)

    return () => window.removeEventListener('resize', onResize)
  }, [debouncedForcedUpdate])

  const visibleColumnCount = useMemo(() => {
    const childColumns = getChildColumns(columns)
    // use current level column if children are empty
    const countableColumns = childColumns.length !== 0 ? childColumns : columns
    return countableColumns.reduce((acc, col) => {
      const parentColumn = findParentColumn(columns, col)
      const isHidden = (parentColumn && getColumnHiddenValue(parentColumn)) || getColumnHiddenValue(col)

      return isHidden ? acc : acc + 1
    }, 0)
  }, [columns, getColumnHiddenValue])

  const { onDragStart, onDragEnd, onDragCancel, items, sortableColumns } = useSortableRows({ rows: tableRows, columns, onSortRows })

  return (
    <div
      ref={virtualContainerRef}
      id="dnd-a11y-container"
      className="GRTable"
      style={{
        height: rowVirtualizer.getTotalSize() ? `${rowVirtualizer.getTotalSize() + (tableBodyRef.current?.offsetTop ?? 0) + scrollbarHeight}px` : 'auto',
        overflowX: showHorizontalScroll ? 'auto' : 'hidden',
      }}
    >
      <Table ref={tableRef} {...tableProps} className="GRTable__table">
        {!hideHeader && (
          <TableHead ref={headRef}>
            <HeaderRow
              columns={sortableColumns}
              onHeaderClick={handleHeaderClick}
              onMeasure={handleColWidthMeasuring}
              defaultSortOrder={defaultSortOrder}
              customTableProps={customProps}
              getColumnHiddenValue={getColumnHiddenValue}
              gridlines={gridlines}
            />
          </TableHead>
        )}

        <TableBody
          ref={tableBodyRef}
          sx={{
            position: 'relative',
            WebkitTransform: 'translateZ(0)',
          }}
        >
          <DndContext
            autoScroll={false}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            modifiers={[restrictToVerticalAxis]}
            onDragCancel={onDragCancel}
            accessibility={{ container: virtualContainerRef.current ?? undefined }}
          >
            {isLoading ? (
              <LoadingStatusRow columnCount={visibleColumnCount} />
            ) : tableRows.length <= 0 ? (
              <StatusRow columnCount={visibleColumnCount}>{noRowsLabel}</StatusRow>
            ) : (
              <SortableContext items={items} strategy={rectSortingStrategy} disabled={!onSortRows}>
                {rowVirtualizer.getVirtualItems().map((vRow) => {
                  const item = displayItems[vRow.index]

                  if (item.type === 'section') {
                    return (
                      <SectionHeaderRow
                        key={`section-${item.sectionId}`}
                        virtualRow={vRow}
                        virtualizer={rowVirtualizer}
                        colSpan={visibleColumnCount + (onSortRows ? 1 : 0)}
                        collapsible={collapsibleSections}
                        collapsed={collapsibleSections && collapsedSectionIds.has(item.sectionId)}
                        onToggle={collapsibleSections ? () => toggleSection(item.sectionId) : undefined}
                      >
                        {renderSectionHeader?.({ sectionId: item.sectionId, count: item.count })}
                      </SectionHeaderRow>
                    )
                  }

                  if (item.type === 'sectionColumnHeader') {
                    return (
                      <SectionColumnHeaderRow
                        key={`section-columns-${item.sectionId}`}
                        sectionId={item.sectionId}
                        virtualRow={vRow}
                        virtualizer={rowVirtualizer}
                        columns={columns}
                        getColumnHiddenValue={getColumnHiddenValue}
                        customTableProps={customProps}
                        includeDragHandleCell={!!onSortRows}
                        onMeasure={hideHeader ? handleColWidthMeasuring : undefined}
                      />
                    )
                  }

                  return (
                    <VirtualRow
                      key={rowIdKey ? getRowKey(vRow.index) : (vRow.key as string) + vRow.index}
                      id={vRow.index + 1}
                      row={item.row}
                      virtualRow={vRow}
                      columns={columns}
                      getCellValue={getCellValue}
                      hoverable={hoverable}
                      striped={striped}
                      style={rowStyle(item.row, item.rowIndex)}
                      className={rowClass(item.row, item.rowIndex)}
                      getColumnHiddenValue={getColumnHiddenValue}
                      gridlines={gridlines}
                      virtualizer={rowVirtualizer}
                      sortable={!!onSortRows}
                      disableSort={!sortable}
                      disableSortTooltip={disableSortTooltip}
                    />
                  )
                })}
              </SortableContext>
            )}
          </DndContext>
        </TableBody>
      </Table>
    </div>
  )
}

export const GRTable = memo(Component) as typeof Component

const LoadingStatusRow: FC<{ columnCount: number }> = ({ columnCount }) => (
  <StatusRow columnCount={columnCount}>
    <CircularProgress color="primary" />
  </StatusRow>
)

// Custom hook for handling forced update for a callback function (used to force table re-draw in this case)
const useForcedCallback = <T,>(callback: (...args: unknown[]) => T, deps: DependencyList) => {
  const [value, setValue] = useState(0)
  const forceUpdate = useCallback(() => setValue((val) => val + 1), [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const forcedCallback = useCallback(callback, [value, ...deps])

  return { forceUpdate, forcedCallback }
}

type UseSortableRowsParams<RowType, CustomPropsType, ColumnIdType = void> = {
  rows: RowType[]
  columns: GRTableColumn<RowType, CustomPropsType, ColumnIdType>[]
  onSortRows?: (rows: RowType[]) => void
}

/**
 * A custom hook that enables row sorting functionality for tables.
 *
 * @template RowType - The type of row data objects
 * @template CustomPropsType - Additional custom props type (can be undefined)
 * @template ColumnIdType - The type for column identifiers (string or void)
 *
 * @param {Object} params - Hook parameters
 * @param {RowType[]} params.rows - The array of row data
 * @param {Array} params.columns - The columns configuration for the table
 * @param {function(RowType[]): void} [params.onSortRows] - Optional callback triggered when rows are reordered
 *
 * @returns {Object} Sorting-related properties and handlers
 * @returns {function} returns.onDragStart - Handler for drag start events
 * @returns {function} returns.onDragEnd - Handler for drag end events
 * @returns {function} returns.onDragCancel - Handler for drag cancel events
 * @returns {UniqueIdentifier[]} returns.items - Array of item identifiers for tracking order
 * @returns {Array} returns.sortableColumns - Columns with drag handle added when sorting is enabled
 *
 * @description
 * This hook manages the state and handlers required for draggable table rows.
 * When `onSortRows` is provided, it automatically adds a drag handle column
 * to the beginning of the columns array.
 */
const useSortableRows = <RowType extends object, CustomPropsType extends object | undefined, ColumnIdType extends string | void>({
  rows,
  columns,
  onSortRows,
}: UseSortableRowsParams<RowType, CustomPropsType, ColumnIdType>) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [items, setItems] = useState<UniqueIdentifier[]>(rows ? rows.map((_, index) => index + 1) : [])
  useEffect(() => {
    setItems(rows ? rows.map((_, index) => index + 1) : [])
  }, [rows])

  const onDragStart = ({ active }: DragStartEvent) => {
    if (!active) {
      return
    }

    setActiveId(active.id)
  }

  const onDragEnd = ({ over }: DragEndEvent) => {
    setActiveId(null)
    const activeIndex = activeId != null ? items.indexOf(activeId) : -1

    if (over) {
      const overIndex = items.indexOf(over.id)
      if (activeIndex !== overIndex) {
        setItems((items) => arrayMove(items, activeIndex, overIndex))
        if (rows) {
          onSortRows?.(arrayMove(rows, activeIndex, overIndex))
        }
      }
    }
  }

  const onDragCancel = () => {
    setActiveId(null)
  }

  const columnsWithDragHandle = useMemo(() => {
    const dragHandleColumn = {
      headerCellProps: { sx: { width: 60 } },
    }

    return onSortRows ? [dragHandleColumn, ...columns] : columns
  }, [columns, onSortRows])

  return { onDragStart, onDragEnd, onDragCancel, items, sortableColumns: columnsWithDragHandle }
}
